import { Fragment, useCallback, useMemo, useState } from "react";
import { useEffect } from "react";
import HeaderController from "../../components/Headers/HeaderController";
import SubmitButton from "../../components/SubmitButton/SubmitButton";
import * as translator from "../../utils/translator"
import ThankyouMainTitleWidget from "./components/ThankyouMainTitleWidget/ThankyouMainTitleWidget";
import ThankyouStepsWidget from "./components/ThankyouStepsWidget/ThankyouStepsWidget";
import AppInstallBanner from "../../components/AppInstallBanner/AppInstallBanner";
import { useCustomer } from "../../customHooks/useCustomer";
import TestLabStepsWidget from "./components/TestLabStepsWidget/TestLabStepsWidget";
import TestLabRateWidget from "./components/TestLabRateWidget/TestLabRateWidget";
import ThankyouVerificationPage from "./components/ThankyouVerificationPage/ThankyouVerificationPage";
import "./Thankyou.css";
import * as http from "../../utils/http";
import * as SessionStorageWrapper from "../../utils/SessionStorageWrapper"
import { MainTracker } from "../../PixelTrackers/MainTracker";
import { useParams, useSearchParams } from "react-router-dom";
import Trustedshops from "../../components/Trustedshops/Trustedshops";
import { EXTERNAL_PHARMACY_INTEGRATIONS_FLOWS, ORDER_FLOWS, SWITZERLAND, THANKYOU_PAGE_VIEW_EVENT } from "../../utils/consts";
import { getWlExperimentTrackingProps, registerWlExperimentSuperProperties } from "../../utils/wlCarePlusExperiment";
import { isPostCheckoutVerificationCountry } from "../../utils/postCheckoutVerification";
import ThankyouCarePlus from "./CarePlus/ThankyouCarePlus";
import ThankyouUkWeightLoss from "./UkWeightLoss/ThankyouUkWeightLoss";
import useThankyouDocuments, { getDocumentsRequired, getOpenTasks } from "./UkWeightLoss/useThankyouDocuments";

const TRUSTEDSHOP_COUNTRIES = ['DE', 'FR'];
// HL-12639: UK Care+ orders (the GB weight_loss_2 funnel) get their own thank-you page; UK WL1 (weight_loss) keeps the existing one.
const UK_CARE_PLUS_MENTION = 'weight_loss_2';
// The View Thank You Page event waits this long at most for the documents check (documents_required).
const PAGE_VIEW_DOCUMENTS_WAIT_MS = 3000;

export default function Thankyou() {

    const params = useParams();
    const hashID = params.order_hash_id;
    const language = params.language
    const country = params.country;
    
    
    let [searchParams] = useSearchParams();
    let thankyouHash = searchParams.get('thankyouHash');
    const isOrderOnlyPrescription = searchParams.get("onlyPrescription") === "1" ? true : false;
    
    const isERecipe = searchParams.get("erecipe") === "1" ? true : false;
    const [isEPrescriptionOnly, setIsEPrescriptionOnly] = useState(false);
    const [isSelfPickUp, setIsSelfPickUp] = useState(false);
    const [isRxSelfPickUp, setIsRxSelfPickUp] = useState(false);
    const [selfPickupPaymentModel, setSelfPickupPaymentModel] = useState<string>('prescription_and_medication');
    const [isCannaleoPreSelected, setIsCannaleoPreSelected] = useState(false);
    const [isSchengen, setIsSchengen] = useState(searchParams.get("cat") === "schengen");
    const [isOtcOnly, setIsOtcOnly] = useState(false);
    const [isTestLab, setIsTestLab] = useState(searchParams.get("cat") === "test_lab");
    const [isExpressShipping, setisExpressShipping] = useState(false);
    const [isPreOrder, setIsPreOrder] = useState(false);
    const [isVideoConsultation, setIsVideoConsultation] = useState(false);
    const [isWaitCustomerAdditionalActions, setIsWaitCustomerAdditionalActions] = useState<boolean | null>(null);
    const isNextDayFromParam = searchParams.get("tpp") === "2";
    const isPaymentPending = searchParams.get("result") === "Pending";
    const [trustedShopData, setTrustedShopData] = useState<any>(null);
    const [isCarePlusOrder, setIsCarePlusOrder] = useState(false);
    const [customerEmail, setCustomerEmail] = useState<string | undefined>(undefined);
    // undefined until the api answers, so a paired customer never sees a flash or a phantom impression
    const [isPairedToMobileApp, setIsPairedToMobileApp] = useState<boolean | undefined>(undefined);
    // Same rule for the placement switches: undefined keeps W1 / W2 hidden.
    const [appInstallPlacements, setAppInstallPlacements] = useState<string[] | undefined>(undefined);
    const { customerState } = useCustomer();
    // Catalog mention: the `cat` URL param right away, the order's category once the api answers.
    const [categoryMention, setCategoryMention] = useState<string | null>(searchParams.get("cat"));
    const isUkWeightLoss = country?.toUpperCase() === "GB" && categoryMention === UK_CARE_PLUS_MENTION && !isPaymentPending;
    const thankyouDocuments = useThankyouDocuments(country, hashID, isUkWeightLoss);
    const [queuedPageViewProps, setQueuedPageViewProps] = useState<Record<string, unknown> | null>(null);
    const [isPageViewWaitOver, setIsPageViewWaitOver] = useState(false);

    // Map of placeholders to replace in thank you page bullets
    const [placeHoldersMap, setPlaceHoldersMap] = useState<Record<string, string>>({});
    
    const relocationString = useMemo(() => {

        if(isEPrescriptionOnly) {
            return `/${country}/account/my-orders/${hashID}/details#pharmacy-selection-container`
        } 
            
        return `/${country}/account/my-orders` 
    },
    [isEPrescriptionOnly,language,country])


    const btnTextTranslationKey = (isEPrescriptionOnly  && !isCannaleoPreSelected) ? "page/button_text_e_prescription" : "page/button_text";
    // CH test-lab orders use the regular page layout with test-lab copy (`_test_lab` keys); other countries keep the test-lab template.
    const isSwissTestLab = isTestLab && country?.toUpperCase() === SWITZERLAND;
    const showTestLabTemplate = isTestLab && !isSwissTestLab;

    const handleNoPendingActions = useCallback(() => {
        setIsWaitCustomerAdditionalActions(false);
    }, []);

    useEffect(() => {
        window.addEventListener("popstate", (e) => {
        e.preventDefault();
        window.location.replace(relocationString);
         })
    });

    useEffect(() => {


            
            http.getFromServer(`thank_you/${country}/${hashID}/${thankyouHash}`, [])
            .then((response) => {
                
                    if (SessionStorageWrapper.getItem("last_thankyou_order") !== hashID){
                        MainTracker.superProperties(response.data.ConversionData.tracking)
                        // HL-10959: Paid Order / thank-you carry wl_experiment + entry_point
                        registerWlExperimentSuperProperties();
                        const wlExperimentProps = getWlExperimentTrackingProps();
                        // HL-12122: V3 Monthly carries plan_type and no tier (the server sends tier for WL2 Care+ only).
                        const isMonthlyPlan = response.data.carePlusPlanType === "monthly";
                        const carePlusThankyouProps = response.data.isCarePlusOrder ? {
                            ...(isMonthlyPlan ? {} : { tier: response.data.carePlusTier ?? null }),
                            plan_type: response.data.carePlusPlanType ?? null,
                            plan_duration: response.data.carePlusPlanDuration ?? null,
                            is_one_time: isMonthlyPlan ? false : !response.data.ConversionData?.order?.is_recurring,
                            plan_fee: response.data.carePlusPlanFee ?? null,
                            medication_price: response.data.carePlusMedicationPrice ?? null,
                            // HL-12264: no plan_variant on UK V3; every other country keeps it.
                            ...(country?.toUpperCase() === "GB" ? {} : { plan_variant: null }),
                        } : {};
                        MainTracker.track('custom', 'ViewThankyouPage', {
                            ...response.data.ConversionData,
                            payment_status: isPaymentPending ? 'not approved' : 'approved',
                            ...wlExperimentProps,
                        });
                        const pageViewProps = {
                            payment_status: isPaymentPending ? 'not approved' : 'approved',
                            ...carePlusThankyouProps,
                            ...wlExperimentProps,
                        };
                        const orderMention = searchParams.get("cat") || response.data.ConversionData?.tagManager?.item_category2;
                        if (country?.toUpperCase() === "GB" && orderMention === UK_CARE_PLUS_MENTION && !isPaymentPending) {
                            // HL-12639: sent with documents_required once the documents check settles.
                            setQueuedPageViewProps(pageViewProps);
                        } else {
                            MainTracker.track('pv', THANKYOU_PAGE_VIEW_EVENT, pageViewProps);
                        }
                        const tagManagerEvents = response.data.tagManagerEvents;
                        for (const event of tagManagerEvents) {
                            MainTracker.track('custom', "ga4_auto_event", event);
                        }

                        SessionStorageWrapper.setItem("last_thankyou_order", hashID);
                    }

                    setCategoryMention(current => current || response.data.ConversionData?.tagManager?.item_category2 || null);
                    setIsCannaleoPreSelected(EXTERNAL_PHARMACY_INTEGRATIONS_FLOWS.includes(response.data.flow));
                    setPlaceHoldersMap(response.data.placeHoldersMap);
                    setisExpressShipping(response.data.isExpressShipping);
                    setIsPreOrder(response.data.isPreOrder);
                    setIsVideoConsultation(response.data.isVideoConsultation);
                    setIsWaitCustomerAdditionalActions(response.data.isWaitCustomerAdditionalActions === true);
                    setIsOtcOnly(response.data.flow === ORDER_FLOWS.OTC_ONLY);
                    setIsTestLab(current => current || response.data.isTestLab === true);
                    setIsSchengen(current => current || response.data.flow === ORDER_FLOWS.SCHENGEN);

                    setTrustedShopData(response.data.trustedShopData);
                    setIsCarePlusOrder(response.data.isCarePlusOrder);
                    setCustomerEmail(response.data.customerEmail);
                    if (typeof response.data.isPairedToMobileApp !== "boolean") {
                        window.clientLogger.warn("Thank-you response has no boolean isPairedToMobileApp, app-install banners stay hidden", { isPairedToMobileApp: response.data.isPairedToMobileApp });
                    }
                    setIsPairedToMobileApp(response.data.isPairedToMobileApp);
                    setAppInstallPlacements(response.data.appInstallPlacements);

                    if (response.data.isRxSelfPickup) {
                        setIsRxSelfPickUp(true);
                        setSelfPickupPaymentModel(response.data.selfPickupPaymentModel ?? 'prescription_and_medication');
                    } else if (response.data.isSelfPickup) {
                        setIsSelfPickUp(true);
                    } else if (response.data.isEPrescriptionOnly) {
                        setIsEPrescriptionOnly(true);
                    }

                    //remove cpn from local storage on purchase
                    window.eventsTracking.setData('cpn', '');
                })
                .catch((err) => {
                    setIsWaitCustomerAdditionalActions(false);
                    if (err.response?.data?.logLevel === "warn"){
                        window.clientLogger.warn("Thanyou Page get error", err)
                    }
                    else{
                        window.clientLogger.error("Thanyou Page get error", err)
                    }
                });

    }, []);

    useEffect(() => {
        if (!queuedPageViewProps) return;
        const timer = setTimeout(() => setIsPageViewWaitOver(true), PAGE_VIEW_DOCUMENTS_WAIT_MS);
        return () => clearTimeout(timer);
    }, [queuedPageViewProps]);

    useEffect(() => {
        if (!queuedPageViewProps || !(thankyouDocuments.settled || isPageViewWaitOver)) return;
        // HL-12997: tasks_open / tasks_count are the open steps the page lists; null when no check came back.
        const tasksOpen = thankyouDocuments.answered ? getOpenTasks(thankyouDocuments) : null;
        MainTracker.track('pv', THANKYOU_PAGE_VIEW_EVENT, {
            ...queuedPageViewProps,
            // Not settled by then: the latest answer (nothing outstanding yet), null only when no check came back.
            documents_required: thankyouDocuments.answered ? getDocumentsRequired(thankyouDocuments) : null,
            tasks_open: tasksOpen,
            tasks_count: tasksOpen ? tasksOpen.length : null,
        });
        setQueuedPageViewProps(null);
    }, [queuedPageViewProps, thankyouDocuments, isPageViewWaitOver]);

    if (isUkWeightLoss) {
        return (
            <ThankyouUkWeightLoss
                language={language}
                country={country}
                hashID={hashID}
                documents={thankyouDocuments}
                onUploadClick={() => MainTracker.track('custom', 'thank_you_upload_clicked', {
                    documents_required: getDocumentsRequired(thankyouDocuments),
                })}
            />
        );
    }

    // An order still waiting for post-checkout ID / body photos (GB/CH post-checkout verification) gets
    // the verification tasks below first; the Care+ page follows once nothing is outstanding.
    const isAwaitingPostCheckoutVerification = isPostCheckoutVerificationCountry(country) && isWaitCustomerAdditionalActions === true;
    if (isCarePlusOrder && !isOrderOnlyPrescription && !isAwaitingPostCheckoutVerification) {
        return (
            <ThankyouCarePlus
                customerEmail={customerEmail}
                country={country}
                language={language}
                isPairedToMobileApp={isPairedToMobileApp}
                appInstallPlacements={appInstallPlacements}
                customerUuid={customerState?.uuid}
                orderHash={hashID}
            />
        )
    }

    return (
                <>
                {showTestLabTemplate && trustedShopData && TRUSTEDSHOP_COUNTRIES.includes(country!.toUpperCase()) && (
                    <style>{`@media (max-width: 767px) { [id^="trustbadge-container"] { display: none !important; } }`}</style>
                )}
                {trustedShopData && TRUSTEDSHOP_COUNTRIES.includes(country!.toUpperCase()) && (
                    <Trustedshops
                    isThankyouPage={!showTestLabTemplate}
                    language={language}
                    trustedShopData={trustedShopData}
                    />
                    )}
                    <HeaderController language={language} showSteps={false} stage={"Treatment"} />
                    {isWaitCustomerAdditionalActions === null ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
                            <div style={{ width: 40, height: 40, border: '4px solid #e0e0e0', borderTop: '4px solid #333', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        </div>
                    ) : isWaitCustomerAdditionalActions ? (
                        <ThankyouVerificationPage
                            country={country!}
                            hashID={hashID!}
                            language={language!}
                            onNoPendingActions={handleNoPendingActions}
                        />
                    ) : <div className="body-container" style={showTestLabTemplate ? { height: "auto" } : {}} hidden={false}>
                        <div className="thankyou-container">

                            <ThankyouMainTitleWidget
                                thankyouTitle={translator.translate(language, "thankyou", "page/title")}
                                thankyouSubTitle={translator.translate(language, "thankyou", "page/subtitle")}
                            />

                            {/* The uuid comes from the client session, which can still be empty right after a payment redirect */}
                            {!isPaymentPending && (
                                // Hidden while payment is pending so the pending notice stays the first thing to act on.
                                <AppInstallBanner
                                    desktopPlacementId="W1"
                                    mobilePlacementId="W2"
                                    language={language!}
                                    country={country}
                                    customerUuid={customerState?.uuid}
                                    isPairedToMobileApp={isPairedToMobileApp}
                                    allowedPlacements={appInstallPlacements}
                                    orderHash={hashID}
                                    className="thankyou-app-install-banner"
                                />
                            )}

                            {isPaymentPending && (
                                <div className="thankyou-pending-banner">
                                    <div className="thankyou-pending-banner-icon">?</div>
                                    <p className="thankyou-pending-banner-title">
                                        {translator.translate(language, "thankyou", "page/pending_banner_title")}
                                    </p>
                                    <p className="thankyou-pending-banner-text">
                                        {translator.translate(language, "thankyou", "page/pending_banner_text")}
                                    </p>
                                    <p className="thankyou-pending-banner-text">
                                        {translator.translate(language, "thankyou", "page/pending_banner_subtext")}
                                    </p>
                                </div>
                            )}

                            {!showTestLabTemplate && (
                                <div className="thankyou-steps-container">
                                    <p className="thankyou-steps-title">
                                        {translator.translate(language, "thankyou", `page/steps_title${isSwissTestLab ? "_test_lab" : ""}`)}
                                    </p>

                                    <ThankyouStepsWidget placeHoldersMap={placeHoldersMap} isTestLab={isSwissTestLab} isSchengen={isSchengen} isCannaleoPreSelected={isCannaleoPreSelected} isSelfPickUp={isSelfPickUp} isRxSelfPickUp={isRxSelfPickUp} selfPickupPaymentModel={selfPickupPaymentModel} isEPrescriptionOnly={isEPrescriptionOnly} isOrderOnlyPrescription={isOrderOnlyPrescription} isERecipe={isERecipe} isExpressShipping={isExpressShipping} language={language} isOtcOnly={isOtcOnly} isVideoConsultation={isVideoConsultation} country={country} isPreOrder={isPreOrder} isNextDayDisplayed={isNextDayFromParam} />

                                    <SubmitButton
                                        id={"submit-button"}
                                        language={language}
                                        placeholder={translator.translate(language, "thankyou", btnTextTranslationKey)}
                                        onSubmit={() => {
                                            window.location.replace(relocationString);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        {showTestLabTemplate && <TestLabStepsWidget language={language} />}
                        {showTestLabTemplate && (
                            <div className="thankyou-container">
                                <TestLabRateWidget language={language} />
                            </div>
                        )}
                        <div id="trustbadgeCustomCheckoutContainer"></div>
                    </div>}
                    
                </>
    )
}
